// message.ts에서 타입 import
import type { ChatMessage, SupportedLocale } from '~~/types/message'
import { computed, ref } from '#imports'
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { db } from '../../firebaseConfig'

// 텍스트에 포함된 문자 범위로 원문 언어를 간단히 추정 (한글/가나 유무 기준)
// 외부 API 호출 없이 저장 시점에 즉시 판별하기 위한 휴리스틱
function detectLanguage(text: string): SupportedLocale {
  const trimmed = (text ?? '').trim()

  if (!trimmed)
    return 'ko'

  for (const char of trimmed) {
    const codePoint = char.codePointAt(0) ?? 0

    // 히라가나(0x3040-0x309F) / 가타카나(0x30A0-0x30FF) → 일본어로 판단
    if (codePoint >= 0x3040 && codePoint <= 0x30FF)
      return 'ja'

    // 한글 음절(0xAC00-0xD7A3) / 자모(0x1100-0x11FF, 0x3130-0x318F) → 한국어로 판단
    if (
      (codePoint >= 0xAC00 && codePoint <= 0xD7A3)
      || (codePoint >= 0x1100 && codePoint <= 0x11FF)
      || (codePoint >= 0x3130 && codePoint <= 0x318F)
    ) {
      return 'ko'
    }
  }

  // 한글/가나 문자가 없으면(라틴 문자 등) 영어로 기본 처리
  return 'en'
}

export const useMessageStore = defineStore('message', () => {
  // 명시적으로 빈 배열로 초기화
  const chat = ref<any[]>([])
  const conversationMessages = ref<any[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const currentView = ref('list')

  // 현재 사용자 정보 가져오기
  const userStore = useUserStore()
  const currentUserId = computed(() => userStore.userStoreData?.uid)
  const currentUserType = computed(() => userStore.userStoreData?.type || 'creator')

  // 🔥 사용자 정보 가져오기 (fetchChats보다 앞에 위치)
  async function getUserInfo(userId: string) {
    // 먼저 advertiser 컬렉션에서 찾기
    try {
      const advertiserDoc = await getDoc(doc(db, 'advertiser', userId))

      if (advertiserDoc.exists()) {
        const data = advertiserDoc.data()

        // 프로필 이미지는 각 컬렉션의 sign 필드 내에 위치함
        const avatarFromSign = (data as any)?.sign?.profileImageUrl

        const result = {
          name: (data as any).name || (data as any).companyName || '광고주(임시)',
          avatar: avatarFromSign || (data as any).profileImageUrl || (data as any).companyLogo || '',
          type: 'advertiser' as const,
        }
        return result
      }
    }
    catch (err) {
      console.error('Error fetching advertiser info for', userId, err)
    }

    // creator 컬렉션에서 찾기
    try {
      const creatorDoc = await getDoc(doc(db, 'creator', userId))

      if (creatorDoc.exists()) {
        const data = creatorDoc.data()

        // 프로필 이미지는 각 컬렉션의 sign 필드 내에 위치함
        const avatarFromSign = (data as any)?.sign?.profileImageUrl

        const result = {
          name: (data as any).name || '크리에이터',
          avatar: avatarFromSign || (data as any).profileImageUrl || '',
          type: 'creator' as const,
        }
        return result
      }
    }
    catch (err) {
      console.error('Error fetching creator info for', userId, err)
    }

    // 찾지 못한 경우 기본값 반환
    return {
      name: '사용자',
      avatar: '',
      type: 'creator' as const,
    }
  }

  // 🔥 기존 채팅방 데이터 업데이트 (임시 함수)
  async function updateChatData(chatId: string, otherUserId: string) {
    try {
      const otherUserInfo = await getUserInfo(otherUserId)
      const chatRef = doc(db, 'chat', chatId)

      await updateDoc(chatRef, {
        otherUserName: otherUserInfo.name,
        otherUserAvatar: otherUserInfo.avatar,
        otherUserType: otherUserInfo.type,
      })
    }
    catch (error) {
      console.error('채팅방 데이터 업데이트 실패:', error)
    }
  }

  // 채팅방 목록 실시간 조회
  async function fetchChats() {
    if (!currentUserId.value) {
      return
    }

    loading.value = true
    error.value = null

    try {
      const chatRef = collection(db, 'chat')

      // 🔥 인덱스 문제를 피하기 위해 단순한 쿼리 사용
      const q = query(
        chatRef,
        where('participants', 'array-contains', currentUserId.value),
      )

      // 실시간 리스너 설정
      const unsubscribe = onSnapshot(q, async (querySnapshot) => {
        const chatList: any[] = []

        for (const chatDoc of querySnapshot.docs) {
          const chatData = chatDoc.data()

          const participants = chatData.participants as string[]
          const otherUserId = participants.find(id => id !== currentUserId.value)

          if (!otherUserId)
            continue

          try {
            // 🔥 상대방 정보를 더 확실하게 가져오기
            const otherUserInfo = await getUserInfo(otherUserId)

            // 🔥 채팅방에 사용자 정보가 없으면 업데이트
            if (!chatData.otherUserName && otherUserInfo?.name) {
              await updateChatData(chatDoc.id, otherUserId)
            }

            // 🔥 사용자 정보를 가져올 수 없으면 채팅방 데이터의 백업 정보 사용
            const userName = otherUserInfo?.name || chatData.otherUserName || '사용자'
            const userAvatar = otherUserInfo?.avatar || chatData.otherUserAvatar || '/icon/default-avatar.png'
            const userType = otherUserInfo?.type || chatData.otherUserType || 'creator'

            // 마지막 메시지 표시 형식 결정
            let lastMessageDisplay = chatData.lastMessage || '메시지를 시작해보세요'
            if (chatData.lastMessage && chatData.lastMessageSenderId) {
              if (chatData.lastMessageSenderId === currentUserId.value) {
                lastMessageDisplay = `나: ${chatData.lastMessage}`
              }
              else {
                lastMessageDisplay = chatData.lastMessage
              }
            }

            chatList.push({
              id: chatDoc.id,
              name: userName,
              description: lastMessageDisplay,
              avatar: userAvatar,
              lastMessageTime: chatData.updatedAt?.toDate() || chatData.createdAt?.toDate() || new Date(),
              badge: userType === 'advertiser' ? '광고주' : '크리에이터',
              unread: false, // TODO: 읽지 않은 메시지 개수 계산
              type: 'received',
              participants,
              otherUserId,
              otherUserType: userType,
              status: chatData.status || 'pending',
              pendingRecipientId: chatData.pendingRecipientId || '',
            })
          }
          catch (userError) {
            console.error('Error fetching user info for chat list:', userError)
            // 🔥 사용자 정보를 가져올 수 없어도 기본값으로 채팅방 표시
            chatList.push({
              id: chatDoc.id,
              name: chatData.otherUserName || '사용자',
              description: chatData.lastMessage || '메시지를 시작해보세요',
              avatar: chatData.otherUserAvatar || '/icon/default-avatar.png',
              lastMessageTime: chatData.updatedAt?.toDate() || chatData.createdAt?.toDate() || new Date(),
              badge: (chatData.otherUserType === 'advertiser') ? '광고주' : '크리에이터',
              unread: false,
              type: 'received',
              participants,
              otherUserId,
              otherUserType: chatData.otherUserType || 'creator',
              status: chatData.status || 'pending',
              pendingRecipientId: chatData.pendingRecipientId || '',
            })
          }
        }

        // 수동으로 시간순 정렬 (인덱스 문제 해결)
        chatList.sort((a, b) => b.lastMessageTime.getTime() - a.lastMessageTime.getTime())

        chat.value = chatList
        loading.value = false
      }, (err: any) => {
        if (err.code === 'failed-precondition' && err.message.includes('index')) {
          error.value = 'Firestore 인덱스가 필요합니다. Firebase Console에서 인덱스를 생성해주세요.'
          console.error('Firestore index required. Please create the index in Firebase Console.')
        }
        else {
          error.value = '채팅 목록을 불러오는데 실패했습니다.'
        }
        console.error('Failed to fetch chat:', err)
        loading.value = false
      })

      // 컴포넌트가 언마운트될 때 리스너 해제를 위해 unsubscribe 함수 반환
      return unsubscribe
    }
    catch (err: any) {
      if (err.code === 'failed-precondition' && err.message.includes('index')) {
        error.value = 'Firestore 인덱스가 필요합니다. Firebase Console에서 인덱스를 생성해주세요.'
        console.error('Firestore index required. Please create the index in Firebase Console.')
      }
      else {
        error.value = '채팅 목록을 불러오는데 실패했습니다.'
      }
      console.error('Failed to fetch chat:', err)
      loading.value = false
    }
  }

  // 사용자 정보 가져오기
  // 특정 채팅방의 메시지 실시간 조회
  async function fetchConversationMessages(chatId: string) {
    loading.value = true
    error.value = null

    // 🔥 새로운 채팅방 선택 시 기존 메시지 초기화
    conversationMessages.value = []

    try {
      const messagesRef = collection(db, 'chat', chatId, 'messages')
      const q = query(messagesRef, orderBy('createdAt', 'asc'))

      // 실시간 리스너 설정
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const messageList: any[] = []

        querySnapshot.forEach((messageDoc) => {
          const messageData = messageDoc.data()
          const isOwn = messageData.senderId === currentUserId.value

          messageList.push({
            id: messageDoc.id,
            text: messageData.text,
            senderId: messageData.senderId,
            createdAt: messageData.createdAt?.toDate() || new Date(),
            isRead: messageData.isRead || false,
            isOwn,
            sourceLanguage: messageData.sourceLanguage ?? null,
            translated: messageData.translated ?? null,
            translationStatus: messageData.translationStatus ?? null,
          })
        })

        conversationMessages.value = messageList
        loading.value = false
      }, (err) => {
        console.error('Failed to fetch conversation messages:', err)
        error.value = '메시지를 불러오는데 실패했습니다.'
        loading.value = false
      })

      // 컴포넌트가 언마운트될 때 리스너 해제를 위해 unsubscribe 함수 반환
      return unsubscribe
    }
    catch (err) {
      console.error('Failed to fetch conversation messages:', err)
      error.value = '메시지를 불러오는데 실패했습니다.'
      loading.value = false
    }
  }

  // 새 채팅방 생성 또는 기존 채팅방 찾기
  async function createOrFindChat(request: any) {
    if (!currentUserId.value)
      throw new Error('로그인이 필요합니다')

    try {
      // 🔥 정확한 채팅방 ID 생성 (항상 동일한 순서로)
      // const chatId = [currentUserId.value, request.targetId].sort().join('_')
      const chatId = userStore.userStoreData?.type === 'advertiser' ? `${currentUserId.value}_${request.targetId}` : `${request.targetId}_${currentUserId.value}`

      // 기존 채팅방이 있는지 확인
      const chatRef = doc(db, 'chat', chatId)
      const chatDoc = await getDoc(chatRef)

      if (chatDoc.exists()) {
        // 기존 채팅방이 있으면 해당 ID 반환
        currentView.value = 'detail'
        return { ...chatDoc.data(), id: chatDoc.id }
      }

      // 새 채팅방 생성 (고정된 ID 사용)
      await setDoc(chatRef, {
        participants: [currentUserId.value, request.targetId],
        lastMessage: request.initialMessage || '',
        lastMessageSenderId: request.initialMessage ? currentUserId.value : '',
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        // 🔥 승인 시스템 제거 - 바로 accepted 상태
        status: 'accepted', // 바로 수락 상태로 설정
        createdBy: currentUserId.value,
        // 🔥 상대방 정보 저장 (빠른 접근을 위해) - undefined 값 방지
        otherUserName: request.targetName || '사용자',
        otherUserAvatar: request.targetAvatar || '',
        otherUserType: request.targetType || 'creator',
        // 현재 사용자 정보도 저장 - undefined 값 방지
        currentUserName: userStore.userStoreData?.name || userStore.userStoreData?.displayName || '사용자',
        currentUserAvatar: userStore.userStoreData?.profileImage || userStore.userStoreData?.photoURL || '',
        currentUserType: currentUserType.value,
      })

      // 초기 메시지가 있으면 직접 추가
      if (request.initialMessage) {
        const messagesRef = collection(db, 'chat', chatId, 'messages')
        await addDoc(messagesRef, {
          chatId,
          text: request.initialMessage,
          sourceLanguage: detectLanguage(request.initialMessage),
          senderId: currentUserId.value,
          receiverId: request.targetId,
          receiverType: request.targetType,
          createdAt: serverTimestamp(),
          isRead: false,
        })
      }

      currentView.value = 'detail'
      return chatId
    }
    catch (err) {
      console.error('Failed to create or find chat:', err)
      throw err
    }
  }

  // 메시지 전송
  async function sendMessage(request: any) {
    if (!currentUserId.value)
      throw new Error('로그인이 필요합니다')

    loading.value = true
    error.value = null

    try {
      // 채팅 상태 확인: 승인되지 않았으면 전송 불가
      const chatRef = doc(db, 'chat', request.chatId)
      const chatSnap = await getDoc(chatRef)
      if (!chatSnap.exists())
        throw new Error('채팅을 찾을 수 없습니다')

      // 🔥 승인 시스템 제거 - 바로 메시지 전송 가능
      // 기존 채팅방이면 자동으로 accepted 상태로 업데이트
      const chatData = chatSnap.data() as any
      if (chatData?.status !== 'accepted') {
        await updateDoc(chatRef, {
          status: 'accepted',
          updatedAt: serverTimestamp(),
        })
      }

      // 메시지 추가
      const messagesRef = collection(db, 'chat', request.chatId, 'messages')
      await addDoc(messagesRef, {
        chatId: request.chatId,
        senderId: currentUserId.value,
        text: request.text,
        sourceLanguage: detectLanguage(request.text),
        createdAt: serverTimestamp(),
        isRead: false,
      })

      // 채팅방 업데이트 (마지막 메시지, 업데이트 시간, 마지막 메시지 발신자)
      await updateDoc(chatRef, {
        lastMessage: request.text,
        lastMessageSenderId: currentUserId.value,
        updatedAt: serverTimestamp(),
      })

      // 실시간 리스너가 자동으로 업데이트하므로 로컬 상태 업데이트 불필요
    }
    catch (err) {
      error.value = '메시지 전송에 실패했습니다.'
      throw err
    }
    finally {
      loading.value = false
    }
  }

  // 채팅 수락
  async function approveChat(chatId: string) {
    if (!currentUserId.value)
      throw new Error('로그인이 필요합니다')
    try {
      const chatRef = doc(db, 'chat', chatId)
      await updateDoc(chatRef, {
        status: 'accepted',
        pendingRecipientId: '',
        approvedAt: serverTimestamp(),
        approvedBy: currentUserId.value,
        updatedAt: serverTimestamp(),
      })
    }
    catch (e) {
      console.error('채팅 수락 실패:', e)
      throw e
    }
  }

  // 채팅 거절
  async function rejectChat(chatId: string) {
    if (!currentUserId.value)
      throw new Error('로그인이 필요합니다')
    try {
      const chatRef = doc(db, 'chat', chatId)
      await updateDoc(chatRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: currentUserId.value,
        updatedAt: serverTimestamp(),
      })
    }
    catch (e) {
      console.error('채팅 거절 실패:', e)
      throw e
    }
  }

  // 메시지 읽음 처리
  async function markAsRead(chatId: string) {
    if (!currentUserId.value)
      return

    try {
      const messagesRef = collection(db, 'chat', chatId, 'messages')
      const q = query(
        messagesRef,
        where('senderId', '!=', currentUserId.value),
        where('isRead', '==', false),
      )

      const querySnapshot = await getDocs(q)
      const batch = writeBatch(db)

      querySnapshot.forEach((messageDoc) => {
        batch.update(messageDoc.ref, { isRead: true })
      })

      await batch.commit()
    }
    catch (err) {
      console.error('Failed to mark messages as read:', err)
    }
  }

  // 계산된 속성들
  const receivedMessages = computed(() => chat.value)
  const sentMessages = computed(() => chat.value)

  // 유틸리티 함수들
  function formatTime(date: Date) {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0)
      return '오늘'
    if (days === 1)
      return '1일 전'
    if (days < 7)
      return `${days}일 전`

    return date.toLocaleDateString('ko-KR')
  }

  function formatDate(date: Date) {
    return `${date.getMonth() + 1}월 ${date.getDate()}일`
  }

  function formatMessageTime(date: Date) {
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
  }

  // 사용자 언어 값을 지원 언어(ko/en/ja)로 정규화
  function normalizeLocale(raw: string | null | undefined): SupportedLocale {
    const code = ((raw ?? '').trim().split('-')[0] ?? '').toLowerCase()

    if (code === 'en')
      return 'en'
    if (code === 'ja')
      return 'ja'

    return 'ko'
  }

  // translated[locale] → translated.ko → text 순으로 표시할 텍스트 선택
  function getMessageDisplayText(msg: Pick<ChatMessage, 'text' | 'translated'>, locale: SupportedLocale): string {
    const translatedForLocale = msg.translated?.[locale]
    if (translatedForLocale && translatedForLocale.trim().length > 0)
      return translatedForLocale

    const translatedKo = msg.translated?.ko
    if (translatedKo && translatedKo.trim().length > 0)
      return translatedKo

    return msg.text
  }

  return {
    // 상태
    chat,
    conversationMessages,
    loading,
    error,

    // 계산된 속성
    receivedMessages,
    sentMessages,
    currentView,

    // 메서드
    fetchChats,
    fetchConversationMessages,
    createOrFindChat,
    sendMessage,
    markAsRead,
    approveChat,
    rejectChat,

    // 유틸리티
    formatTime,
    formatDate,
    formatMessageTime,
    normalizeLocale,
    getMessageDisplayText,
    detectLanguage,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useContractStore, import.meta.hot))
